import { date } from "quasar"
import { KeyFunctionMap, RxCollection, RxCollectionCreator, RxDocument, RxJsonSchema } from "rxdb"
import { UnsubmittedWork } from "src/util/types/dataModels/unsubmittedWork"

//region Types

export interface UnsubmittedWorkCollectionMethods extends KeyFunctionMap {
  getOldestWorkDays: () => Promise<number>
  hasUnsubmittedWork: () => Promise<boolean>
}

export type UnsubmittedWorkDocument = RxDocument<UnsubmittedWork, KeyFunctionMap>

export type UnsubmittedWorkCollection = RxCollection<
  UnsubmittedWork,
  KeyFunctionMap,
  UnsubmittedWorkCollectionMethods
>

//endregion

/**
 * Schema for unsubmitted work collections
 */
export const unsubmittedWorkSchema: RxJsonSchema<UnsubmittedWork> = {
  title: "Unsubmitted work schema",
  description: "Collection of all the unsubmitted work",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true }, // UUID
    entity_id: { type: "string", maxLength: 36 }, // UUID
    entity_type: { type: "string" }, // Item or Area
    work_type: { type: "string" },
    declared_dt: { type: "string", maxLength: 64 },
    work_domain: { type: "string", maxLength: 36 }, // UUID
    procedure_results: { type: "object" },
    title: { type: "string", maxLength: 100 },
    description: { type: "string", maxLength: 2048 },
    work_order_id: { type: "string", maxLength: 36 },
    unsubmitted_work_issues: {
      type: "array",
      items: { type: "string", maxLength: 36 },
    },
    attachments: {
      type: "array",
      items: {
        type: "string",
      },
    },
  },
  required: ["id", "entity_id", "entity_type", "work_type", "declared_dt"],
  indexes: ["declared_dt"],
}

export const unsubmittedWorkCollectionMethods: UnsubmittedWorkCollectionMethods = {
  async getOldestWorkDays(this: UnsubmittedWorkCollection): Promise<number> {
    const data = await this.find().exec()
    // NOTE: Would directly fetching the first entity from the DB be sufficient?
    const oldest = data.sort((a, b) => a.declared_dt.localeCompare(b.declared_dt))[0]

    if (!oldest) return 0

    return date.getDateDiff(new Date(), oldest.declared_dt, "days")
  },

  async hasUnsubmittedWork(this: UnsubmittedWorkCollection): Promise<boolean> {
    const unsubmittedWorkCount = await this.count().exec()

    return Boolean(unsubmittedWorkCount)
  },
}

/**
 * A collection creator that can be passed to the database when setting up collections
 */
export const unsubmittedWorkCollectionCreator: RxCollectionCreator = {
  schema: unsubmittedWorkSchema,
  statics: unsubmittedWorkCollectionMethods,
}
